// Areas Page - Yoga classes serving Somerset & Wiltshire
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import { usePageMeta, generateLocationSchema, SchemaScript } from "../components/shared/SEOHelper";

const areas = [
  {
    name: "Bath",
    slug: "/areas/yoga-bath",
    time: "25 min",
    description: "Small, welcoming yoga classes in Frome, about 25 minutes from Bath. A calm and supportive environment with space for individual guidance.",
    keywords: "yoga bath, yoga classes bath"
  },
  {
    name: "Trowbridge",
    slug: "/areas/yoga-trowbridge",
    time: "15 min",
    description: "Just over the Wiltshire border. The A361 makes it a straightforward drive — you can be here in under 15 minutes.",
    keywords: "yoga trowbridge, yoga classes trowbridge"
  },
  {
    name: "Warminster",
    slug: "/areas/yoga-warminster",
    time: "20 min",
    description: "A pleasant 20-minute drive through the countryside. Several of my regular students make this journey each week.",
    keywords: "yoga warminster, yoga classes warminster"
  },
  {
    name: "Westbury",
    slug: "/areas/yoga-westbury",
    time: "20 min",
    description: "Easy access via the A36. Westbury residents often find it quicker to reach Frome than Bath for yoga classes.",
    keywords: "yoga westbury, yoga classes westbury"
  },
  {
    name: "Shepton Mallet",
    slug: "/areas/yoga-shepton-mallet",
    time: "15 min",
    description: "Neighbours in the Somerset landscape. Quick and easy to get here via the A361 — just 15 minutes door to door.",
    keywords: "yoga shepton mallet, yoga classes shepton mallet"
  },
  {
    name: "Wells",
    slug: "/areas/yoga-wells",
    time: "25 min",
    description: "A scenic drive through the Mendips. Small, welcoming yoga classes with space for individual guidance.",
    keywords: "yoga wells, yoga classes wells"
  },
  {
    name: "Radstock",
    slug: "/areas/yoga-radstock",
    time: "15 min",
    description: "Close by and easy to reach. Radstock residents are some of my most regular students.",
    keywords: "yoga radstock, yoga classes radstock"
  },
  {
    name: "Midsomer Norton",
    slug: "/areas/yoga-midsomer-norton",
    time: "15 min",
    description: "Twin town with Radstock, just 15 minutes via the A362. Easy access to personal, small-group yoga.",
    keywords: "yoga midsomer norton"
  },
  {
    name: "Mells",
    slug: "/areas/yoga-mells",
    time: "5 min",
    description: "Beautiful Somerset village just 5 minutes from my studio. Couldn't be more convenient.",
    keywords: "yoga mells"
  },
  {
    name: "Nunney",
    slug: "/areas/yoga-nunney",
    time: "5 min",
    description: "The castle village is practically on my doorstep — just 5 minutes away.",
    keywords: "yoga nunney"
  },
  {
    name: "Beckington",
    slug: "/areas/yoga-beckington",
    time: "10 min",
    description: "On the A36 towards Bath, just 10 minutes south. Closer than driving into Bath.",
    keywords: "yoga beckington"
  },
  {
    name: "Bradford-on-Avon",
    slug: "/areas/yoga-bradford-on-avon",
    time: "20 min",
    description: "Historic Wiltshire town, 20 minutes through Trowbridge. Personal yoga closer than Bath.",
    keywords: "yoga bradford on avon"
  },
];

const AreasPage = () => {
  usePageMeta({
    title: "Yoga Near Bath, Trowbridge & Somerset | Annelie Lily",
    description: "Yoga classes in Frome serving Bath, Trowbridge, Warminster, Westbury, Shepton Mallet, Wells & Radstock.",
    canonical: "https://annelielily.com/areas"
  });

  const locationSchema = generateLocationSchema({
    townName: "Somerset & Wiltshire",
    description: "Yoga classes in Frome serving the surrounding areas of Somerset and Wiltshire including Bath, Trowbridge, Warminster, and more.",
    url: "https://annelielily.com/areas"
  });

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Areas We Serve
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Classes Near You
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Based in Frome, Somerset, I welcome students from across the surrounding area. 
              If you're looking for small, personal yoga classes where you're not just another 
              face in the crowd, you're closer than you think.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Little Keyford Lane, Frome
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Car className="w-4 h-4 text-gold" />
                15-25 min from most areas
              </span>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Areas Grid */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Towns & Villages We Serve
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Click on your area to learn more about making the journey to Frome
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {areas.map((area, index) => (
              <motion.div
                key={area.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={area.slug}
                  className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 hover:shadow-md transition-all h-full group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-xl text-neutral-800 group-hover:text-gold transition-colors">
                      {area.name}
                    </h3>
                    <span className="text-xs bg-gold/10 text-gold px-3 py-1 rounded-full font-medium">
                      {area.time}
                    </span>
                  </div>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">
                    {area.description}
                  </p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Yoga near {area.name} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* About the Classes */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Small Classes, Individual Attention
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                My classes are kept small — usually around 6-8 people — so there's space 
                for individual guidance. This allows me to offer adjustments, suggest 
                modifications, and adapt the practice to suit everyone in the room.
              </p>
              <p>
                Each class includes a mix of yoga postures, breathing practices, and 
                relaxation, finishing with a guided yoga nidra. Whether you're <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">new to 
                yoga</Link> or have been practising for years, you'll find a welcoming environment.
              </p>
              <p>
                Many of my students enjoy the journey through the countryside as a way 
                to transition from the busyness of the day — arriving already feeling 
                a little more settled. I welcome students from <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link>, <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link>, and 
                the surrounding areas.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* County Hubs */}
      <section className="py-12 px-6 md:px-12 bg-cream border-t border-gold/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/areas/yoga-somerset"
              className="block bg-gold/5 p-6 rounded-xl border border-gold/20 hover:border-gold/40 transition-all group"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                Yoga in Somerset →
              </h3>
              <p className="text-neutral-600 text-sm">
                All Somerset locations: Bath, Wells, Shepton Mallet, Radstock, Midsomer Norton, Mells, Nunney & more
              </p>
            </Link>
            <Link
              to="/areas/yoga-wiltshire"
              className="block bg-gold/5 p-6 rounded-xl border border-gold/20 hover:border-gold/40 transition-all group"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                Yoga in Wiltshire →
              </h3>
              <p className="text-neutral-600 text-sm">
                All Wiltshire locations: Trowbridge, Warminster, Westbury, Bradford-on-Avon & more
              </p>
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Try a Class?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch and I'll help you figure out which class suits you best, 
              and give you directions to make your first visit easy.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/services" className="btn-secondary">
                View Classes
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AreasPage;
